import { useEffect, useMemo, useState, useCallback } from 'react';
import { safeGet, safeSet } from '../../utils/safeStorage';
import { STORAGE_KEYS } from './storageKeys';
import { regenerateAllTasks, TASK_STATUS } from '../../data/taskEngine';
import { RISK_UNIVERSE } from '../../data/riskUniverse';
import { DEMO_USERS } from '../../data/mockData';

/**
 * useTasksState — explicit task collection auto-derived from upstream state.
 *
 * The tasks array is regenerated (idempotently) on every upstream change:
 *   • applicability map
 *   • risk evaluations (confirmed flags)
 *   • masters (pcc / fraud / dlc)
 *   • riskPublished
 *   • formResponses (so status flips reflect immediately)
 *
 * Status overrides written by the user (e.g. manual reject) persist via the
 * `overrides` map keyed by taskId. The base generation result is merged with
 * those overrides on every regeneration.
 */
export function useTasksState({ applicability, evaluations, masters, formResponses, riskPublished }) {
  const [overrides, setOverrides] = useState(() => safeGet(STORAGE_KEYS.taskOverrides, {}));

  useEffect(() => { safeSet(STORAGE_KEYS.taskOverrides, overrides); }, [overrides]);

  const tasks = useMemo(() => {
    const base = regenerateAllTasks({
      applicability,
      evaluations,
      masters,
      formResponses,
      riskPublished,
      riskUniverseMasters: RISK_UNIVERSE,
      demoUsers: DEMO_USERS,
    });
    return base.map((t) => {
      const o = overrides[t.taskId];
      if (!o) return t;
      return {
        ...t,
        status: o.status || t.status,
        dueDate: o.dueDate || t.dueDate,
        history: [...(t.history || []), ...(o.historyAppend || [])],
      };
    });
  }, [applicability, evaluations, masters, formResponses, riskPublished, overrides]);

  const updateTaskStatus = useCallback((taskId, status, note) => {
    setOverrides((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        status,
        historyAppend: [
          ...(prev[taskId]?.historyAppend || []),
          { at: new Date().toISOString(), action: `Status → ${status}${note ? ` · ${note}` : ''}` },
        ],
      },
    }));
  }, []);

  const reassignTask = useCallback((taskId, dueDate, note) => {
    setOverrides((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        dueDate,
        historyAppend: [
          ...(prev[taskId]?.historyAppend || []),
          { at: new Date().toISOString(), action: `Due date → ${new Date(dueDate).toLocaleDateString()}${note ? ` · ${note}` : ''}` },
        ],
      },
    }));
  }, []);

  const resetTaskOverrides = useCallback(() => setOverrides({}), []);

  return { tasks, updateTaskStatus, reassignTask, resetTaskOverrides, TASK_STATUS };
}
